import React, { useState, useEffect } from 'react'
import { getUserInfoSync, storeAuthData, getAccessToken, getRefreshToken } from '../../config/api'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import { receiptsDB } from '../../db/indexedDB'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const Profile = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [profileLoading, setProfileLoading] = useState(true)
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [offlineSales, setOfflineSales] = useState([])
  const [offlineSalesLoading, setOfflineSalesLoading] = useState(false)
  const [expandedRows, setExpandedRows] = useState(new Set())
  const isFirstLogin = user?.first_login === true

  useEffect(() => {
    // Fetch profile data from API
    const fetchProfile = async () => {
      try {
        setProfileLoading(true)
        const response = await apiClient.get(API_ENDPOINTS.PROFILE)
        const data = response.data
        
        // Map API response to user object format
        const mappedUser = {
          id: data.user?.id || data.id,
          code: data.code,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.user?.email,
          role: data.user?.role,
          role_display: data.user?.role_display,
          is_active: data.user?.is_active,
          date_joined: data.user?.date_joined,
          first_login: data.user?.first_login,
          branch: data.user?.branch || data.branch,
          warehouse: data.user?.warehouse || data.warehouse,
          branch_name: data.user?.branch_name || data.branch_name,
          warehouse_name: data.user?.warehouse_name,
          employee_name: data.user?.employee_name,
          // Keep existing user info as fallback
          ...getUserInfoSync()
        }
        
        setUser(mappedUser)
        
        // If first login, automatically show change password tab
        if (mappedUser.first_login === true) {
          // Wait for DOM to be ready, then trigger the change password tab
          setTimeout(() => {
            const changePasswordTab = document.querySelector('[data-bs-target="#profile-change-password"]')
            if (changePasswordTab) {
              changePasswordTab.click()
            }
          }, 100)
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
        // Fallback to stored user info if API fails
        const userInfo = getUserInfoSync()
        if (userInfo) {
          setUser(userInfo)
          if (userInfo.first_login === true) {
            setTimeout(() => {
              const changePasswordTab = document.querySelector('[data-bs-target="#profile-change-password"]')
              if (changePasswordTab) {
                changePasswordTab.click()
              }
            }, 100)
          }
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load profile data. Please try again.'
          })
        }
      } finally {
        setProfileLoading(false)
      }
    }

    fetchProfile()
  }, [])

  // Fetch offline sales waiting to be synced
  useEffect(() => {
    const fetchOfflineSales = async () => {
      try {
        setOfflineSalesLoading(true)
        const unsyncedReceipts = await receiptsDB.getUnsynced()
        // Sort by created_at descending (newest first)
        const sorted = unsyncedReceipts.sort((a, b) => {
          const dateA = new Date(a.created_at || 0)
          const dateB = new Date(b.created_at || 0)
          return dateB - dateA
        })
        setOfflineSales(sorted)
      } catch (error) {
        console.error('Error fetching offline sales:', error)
        setOfflineSales([])
      } finally {
        setOfflineSalesLoading(false)
      }
    }

    fetchOfflineSales()
    
    // Refresh every 5 seconds to show updated sync status
    const interval = setInterval(fetchOfflineSales, 5000)
    
    return () => clearInterval(interval)
  }, [])

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    })
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!passwordData.old_password || !passwordData.new_password || !passwordData.confirm_password) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please fill in all fields'
      })
      return
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'New password and confirm password do not match'
      })
      return
    }

    if (passwordData.new_password.length < 8) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'New password must be at least 8 characters long'
      })
      return
    }

    setLoading(true)

    try {
      const response = await apiClient.post(API_ENDPOINTS.CHANGE_PASSWORD, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      })

      // If first login, update user info
      if (isFirstLogin && user) {
        // Check if response includes updated user info
        let updatedUser
        if (response.data?.user) {
          updatedUser = response.data.user
        } else {
          // Fallback: update locally (backend should update first_login)
          updatedUser = { ...user, first_login: false }
        }
        
        // Get tokens from api config
        const accessToken = await getAccessToken()
        const refreshToken = await getRefreshToken()
        
        if (accessToken && refreshToken) {
          await storeAuthData(accessToken, refreshToken, updatedUser)
          setUser(updatedUser)
        }
      }

      // Success
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: isFirstLogin 
          ? 'Password changed successfully! You can now access all features. The page will reload shortly.' 
          : 'Password changed successfully!',
        timer: isFirstLogin ? 2000 : 3000,
        showConfirmButton: false
      }).then(() => {
        // Reload page after first login password change to update all components
        if (isFirstLogin) {
          window.location.reload()
        }
      })

      // Reset form
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: ''
      })

    } catch (error) {
      // Error handling
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.response?.data?.detail ||
                          'Failed to change password. Please check your current password and try again.'
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Format user role for display
  const formatRole = (role) => {
    if (!role) return 'User'
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  }

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.full_name) return user.full_name
    if (user?.first_name && user?.last_name) return `${user.first_name} ${user.last_name}`
    if (user?.username) return user.username
    if (user?.email) return user.email.split('@')[0]
    return 'User'
  }

  // Format date/time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return dateString
    }
  }

  // Get payment method badge class
  const getPaymentMethodBadgeClass = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'bg-primary'
      case 'card':
        return 'bg-info'
      case 'mobile':
        return 'bg-success'
      default:
        return 'bg-secondary'
    }
  }

  // Get sync status badge class
  const getSyncStatusBadgeClass = (status) => {
    switch (status) {
      case 'syncing':
        return 'bg-warning'
      case 'pending':
        return 'bg-secondary'
      case 'failed':
        return 'bg-danger'
      default:
        return 'bg-secondary'
    }
  }

  // Toggle row expansion
  const toggleRow = (saleId) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(saleId)) {
      newExpanded.delete(saleId)
    } else {
      newExpanded.add(saleId)
    }
    setExpandedRows(newExpanded)
  }

  // Generate PDF for unsynced sales
  const generatePDF = () => {
    if (offlineSales.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no unsynced sales to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      const doc = new jsPDF('portrait', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 14
      let yPos = margin
      
      // Add title
      doc.setFontSize(18)
      doc.setTextColor(40, 40, 40)
      doc.setFont(undefined, 'bold')
      doc.text('Unsynced Sales Report', margin, yPos)
      yPos += 8
      
      // Add metadata
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.setFont(undefined, 'normal')
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPos)
      yPos += 5
      doc.text(`Total Unsynced Sales: ${offlineSales.length}`, margin, yPos)
      yPos += 10
      
      // Process each sale
      offlineSales.forEach((sale, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 80) {
          doc.addPage()
          yPos = margin
        }
        
        // Sale header
        doc.setFontSize(12)
        doc.setTextColor(40, 40, 40)
        doc.setFont(undefined, 'bold')
        const receiptNumber = sale.receipt_number || `WB-${sale.id}`
        doc.text(`Receipt #${receiptNumber}`, margin, yPos)
        yPos += 6
        
        // Sale details
        doc.setFontSize(9)
        doc.setTextColor(60, 60, 60)
        doc.setFont(undefined, 'normal')
        doc.text(`Date: ${formatDateTime(sale.created_at)}`, margin, yPos)
        doc.text(`Payment: ${(sale.payment_method || 'CASH').toUpperCase()}`, margin + 60, yPos)
        yPos += 5
        doc.text(`Branch: ${sale.branch_name || '-'}`, margin, yPos)
        doc.text(`Cashier: ${sale.cashier_name || '-'}`, margin + 60, yPos)
        yPos += 5
        if (sale.customer?.name) {
          doc.text(`Customer: ${sale.customer.name}`, margin, yPos)
          yPos += 5
        }
        doc.text(`Status: ${sale.sync_status === 'syncing' ? 'Syncing...' : sale.sync_status === 'failed' ? 'Failed' : 'Pending'}`, margin, yPos)
        yPos += 7
        
        // Items table
        if (sale.items && sale.items.length > 0) {
          const itemsData = sale.items.map(item => {
            const itemPrice = parseFloat(item.price || 0)
            const itemQuantity = parseFloat(item.quantity || 0)
            const itemSubtotal = itemPrice * itemQuantity
            
            return [
              item.name || 'N/A',
              itemQuantity.toFixed(2),
              `$${itemPrice.toFixed(2)}`,
              `$${itemSubtotal.toFixed(2)}`
            ]
          })
          
          doc.autoTable({
            startY: yPos,
            head: [['Product Name', 'Quantity', 'Unit Price', 'Subtotal']],
            body: itemsData,
            theme: 'striped',
            headStyles: {
              fillColor: [66, 139, 202],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 8
            },
            bodyStyles: {
              fontSize: 7
            },
            alternateRowStyles: {
              fillColor: [245, 245, 245]
            },
            margin: { top: yPos, left: margin, right: margin },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap'
            },
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 30, halign: 'right' },
              2: { cellWidth: 30, halign: 'right' },
              3: { cellWidth: 30, halign: 'right' }
            }
          })
          
          yPos = doc.lastAutoTable.finalY + 5
        } else {
          doc.setFontSize(8)
          doc.setTextColor(120, 120, 120)
          doc.text('No items recorded', margin, yPos)
          yPos += 5
        }
        
        // Financial summary
        doc.setFontSize(8)
        doc.setTextColor(60, 60, 60)
        const subtotal = parseFloat(sale.subtotal || sale.total || 0)
        const discount = parseFloat(sale.discount || 0)
        const total = parseFloat(sale.total || 0)
        
        doc.text(`Subtotal: $${subtotal.toFixed(2)}`, pageWidth - margin - 50, yPos)
        yPos += 4
        if (discount > 0) {
          doc.setTextColor(200, 0, 0)
          doc.text(`Discount: -$${discount.toFixed(2)}`, pageWidth - margin - 50, yPos)
          doc.setTextColor(60, 60, 60)
          yPos += 4
        }
        doc.setFont(undefined, 'bold')
        doc.text(`Total: $${total.toFixed(2)}`, pageWidth - margin - 50, yPos)
        doc.setFont(undefined, 'normal')
        yPos += 8
        
        // Payment details
        if (sale.amount_received || sale.change_due) {
          doc.setFontSize(8)
          doc.setTextColor(60, 60, 60)
          if (sale.amount_received) {
            doc.text(`Amount Received: $${parseFloat(sale.amount_received).toFixed(2)}`, pageWidth - margin - 50, yPos)
            yPos += 4
          }
          if (sale.change_due) {
            doc.setTextColor(0, 150, 0)
            doc.text(`Change Due: $${parseFloat(sale.change_due).toFixed(2)}`, pageWidth - margin - 50, yPos)
            doc.setTextColor(60, 60, 60)
            yPos += 4
          }
        }
        
        // Separator line between sales
        if (index < offlineSales.length - 1) {
          doc.setDrawColor(200, 200, 200)
          doc.line(margin, yPos, pageWidth - margin, yPos)
          yPos += 5
        }
      })
      
      // Add summary at the end
      if (yPos > pageHeight - 40) {
        doc.addPage()
        yPos = margin
      }
      
      doc.setDrawColor(0, 0, 0)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 5
      
      doc.setFontSize(10)
      doc.setFont(undefined, 'bold')
      doc.setTextColor(40, 40, 40)
      const grandTotal = offlineSales.reduce((sum, sale) => sum + (parseFloat(sale.total) || 0), 0)
      doc.text(`Grand Total: $${grandTotal.toFixed(2)}`, pageWidth - margin - 50, yPos)
      
      // Save PDF
      const fileName = `unsynced-sales-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded',
        text: `Unsynced sales report has been downloaded as ${fileName}`,
        timer: 2000,
        showConfirmButton: false
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Profile</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item active">Profile</li>
          </ol>
        </nav>
      </div>
      {isFirstLogin && (
        <div className="alert alert-warning alert-dismissible fade show" role="alert" style={{ marginBottom: '20px' }}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>First Login Required:</strong> Please change your password to continue. You will not be able to access other features until you change your password.
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      )}
      <section className="section profile">
        <div className="row">
          <div className="col-xl-4">
            <div className="card">
              <div className="card-body profile-card pt-4 d-flex flex-column align-items-center">
                <img src="/assets/img/profile-img.jpg" alt="Profile" className="rounded-circle" />
                <h2>{profileLoading ? 'Loading...' : (user ? getUserDisplayName() : 'User')}</h2>
                <h3>{profileLoading ? 'Loading...' : (user ? (user.role_display || formatRole(user.role)) : 'User')}</h3>
                <div className="social-links mt-2">
                  <a href="#" className="twitter"><i className="bi bi-twitter"></i></a>
                  <a href="#" className="facebook"><i className="bi bi-facebook"></i></a>
                  <a href="#" className="instagram"><i className="bi bi-instagram"></i></a>
                  <a href="#" className="linkedin"><i className="bi bi-linkedin"></i></a>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-8">
            <div className="card">
              <div className="card-body pt-3">
                <ul className="nav nav-tabs nav-tabs-bordered">
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${!isFirstLogin ? 'active' : ''}`} 
                      data-bs-toggle="tab" 
                      data-bs-target="#profile-overview"
                      disabled={isFirstLogin}
                      style={isFirstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${isFirstLogin ? 'active' : ''}`} 
                      data-bs-toggle="tab" 
                      data-bs-target="#profile-change-password"
                    >
                      Change Password
                    </button>
                  </li>
                </ul>
                <div className="tab-content pt-2">
                  <div className={`tab-pane fade ${!isFirstLogin ? 'show active' : ''} profile-overview`} id="profile-overview">
                    <h5 className="card-title">About</h5>
                    <p className="small fst-italic">Profile information and details</p>

                    <h5 className="card-title">Profile Details</h5>

                    <div className="row">
                      <div className="col-lg-3 col-md-4 label ">Full Name</div>
                      <div className="col-lg-9 col-md-8">{profileLoading ? 'Loading...' : (user ? getUserDisplayName() : 'N/A')}</div>
                    </div>

                    {user?.username && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Username</div>
                        <div className="col-lg-9 col-md-8">{user.username}</div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-lg-3 col-md-4 label">Role</div>
                      <div className="col-lg-9 col-md-8">{profileLoading ? 'Loading...' : (user ? (user.role_display || formatRole(user.role)) : 'N/A')}</div>
                    </div>

                    {user?.phone_number && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Phone</div>
                        <div className="col-lg-9 col-md-8">{user.phone_number}</div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-lg-3 col-md-4 label">Email</div>
                      <div className="col-lg-9 col-md-8">{user?.email || 'N/A'}</div>
                    </div>

                    {user?.code && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Employee Code</div>
                        <div className="col-lg-9 col-md-8">{user.code}</div>
                      </div>
                    )}

                    {user?.branch_name && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Branch</div>
                        <div className="col-lg-9 col-md-8">{user.branch_name}</div>
                      </div>
                    )}

                    {user?.warehouse_name && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Warehouse</div>
                        <div className="col-lg-9 col-md-8">{user.warehouse_name}</div>
                      </div>
                    )}

                    {user?.date_joined && (
                      <div className="row">
                        <div className="col-lg-3 col-md-4 label">Date Joined</div>
                        <div className="col-lg-9 col-md-8">{new Date(user.date_joined).toLocaleDateString()}</div>
                      </div>
                    )}

                  </div>

                  <div className={`tab-pane fade ${isFirstLogin ? 'show active' : ''} pt-3`} id="profile-change-password">
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="row mb-3">
                        <label htmlFor="old_password" className="col-md-4 col-lg-3 col-form-label">Current Password</label>
                        <div className="col-md-8 col-lg-9">
                          <input 
                            name="old_password" 
                            type="password" 
                            className="form-control" 
                            id="old_password"
                            value={passwordData.old_password}
                            onChange={handlePasswordChange}
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="new_password" className="col-md-4 col-lg-3 col-form-label">New Password</label>
                        <div className="col-md-8 col-lg-9">
                          <input 
                            name="new_password" 
                            type="password" 
                            className="form-control" 
                            id="new_password"
                            value={passwordData.new_password}
                            onChange={handlePasswordChange}
                            required
                            minLength={8}
                            disabled={loading}
                          />
                          <small className="form-text text-muted">Password must be at least 8 characters long</small>
                        </div>
                      </div>

                      <div className="row mb-3">
                        <label htmlFor="confirm_password" className="col-md-4 col-lg-3 col-form-label">Re-enter New Password</label>
                        <div className="col-md-8 col-lg-9">
                          <input 
                            name="confirm_password" 
                            type="password" 
                            className="form-control" 
                            id="confirm_password"
                            value={passwordData.confirm_password}
                            onChange={handlePasswordChange}
                            required
                            minLength={8}
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="text-center">
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? 'Changing Password...' : 'Change Password'}
                        </button>
                      </div>
                    </form>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offline Sales Waiting to Sync */}
      <section className="section">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="card-title mb-1">
                      <i className="bi bi-cloud-upload me-2"></i>
                      Offline Sales Waiting to Sync
                    </h5>
                    <p className="text-muted small mb-0">
                      These sales were created while offline and are waiting to be synced to the server.
                    </p>
                  </div>
                  {offlineSales.length > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={generatePDF}
                      disabled={offlineSalesLoading}
                    >
                      <i className="bi bi-download me-2"></i>
                      Download PDF
                    </button>
                  )}
                </div>

                {offlineSalesLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : offlineSales.length === 0 ? (
                  <div className="alert alert-info mb-0" role="alert">
                    <i className="bi bi-check-circle me-2"></i>
                    No offline sales waiting to sync. All sales have been synchronized.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover align-middle">
                      <thead>
                        <tr className="table-light">
                          <th style={{ width: '30px' }}></th>
                          <th className="text-uppercase small text-muted">Receipt #</th>
                          <th className="text-uppercase small text-muted">Date & Time</th>
                          <th className="text-uppercase small text-muted">Items</th>
                          <th className="text-end text-uppercase small text-muted">Total</th>
                          <th className="text-uppercase small text-muted">Payment</th>
                          <th className="text-uppercase small text-muted">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offlineSales.map((sale) => (
                          <React.Fragment key={sale.id}>
                            <tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleRow(sale.id)}
                            >
                              <td>
                                <i
                                  className={`bi bi-chevron-${expandedRows.has(sale.id) ? 'down' : 'right'}`}
                                ></i>
                              </td>
                              <td>
                                <span className="fw-semibold">
                                  {sale.receipt_number || `WB-${sale.id}`}
                                </span>
                              </td>
                              <td>
                                <small>{formatDateTime(sale.created_at)}</small>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark">
                                  {sale.items?.length || 0} item{sale.items?.length !== 1 ? 's' : ''}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className="fw-semibold">
                                  {Number(sale.total || 0).toLocaleString(undefined, {
                                    style: 'currency',
                                    currency: 'USD'
                                  })}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getPaymentMethodBadgeClass(sale.payment_method)}`}>
                                  {sale.payment_method?.toUpperCase() || 'CASH'}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${getSyncStatusBadgeClass(sale.sync_status)}`}>
                                  {sale.sync_status === 'syncing' ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Syncing...
                                    </>
                                  ) : sale.sync_status === 'failed' ? (
                                    'Failed'
                                  ) : (
                                    'Pending'
                                  )}
                                </span>
                              </td>
                            </tr>
                            {expandedRows.has(sale.id) && (
                              <tr>
                                <td colSpan="7" className="bg-light">
                                  <div className="p-3">
                                    <h6 className="mb-3">
                                      <i className="bi bi-list-ul me-2"></i>
                                      Sale Items
                                    </h6>
                                    {sale.items && sale.items.length > 0 ? (
                                      <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                          <thead className="table-secondary">
                                            <tr>
                                              <th>Product Name</th>
                                              <th className="text-end">Quantity</th>
                                              <th className="text-end">Unit Price</th>
                                              <th className="text-end">Subtotal</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sale.items.map((item, index) => {
                                              const itemPrice = parseFloat(item.price || 0)
                                              const itemQuantity = parseFloat(item.quantity || 0)
                                              const itemSubtotal = itemPrice * itemQuantity
                                              
                                              return (
                                                <tr key={index}>
                                                  <td>
                                                    <div className="fw-semibold">
                                                      {item.name || 'N/A'}
                                                    </div>
                                                    {item.product_id && (
                                                      <small className="text-muted">
                                                        ID: {item.product_id}
                                                      </small>
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    {itemQuantity.toLocaleString(undefined, {
                                                      minimumFractionDigits: 0,
                                                      maximumFractionDigits: 2
                                                    })}
                                                  </td>
                                                  <td className="text-end">
                                                    {itemPrice.toLocaleString(undefined, {
                                                      style: 'currency',
                                                      currency: 'USD'
                                                    })}
                                                  </td>
                                                  <td className="text-end">
                                                    <strong>
                                                      {itemSubtotal.toLocaleString(undefined, {
                                                        style: 'currency',
                                                        currency: 'USD'
                                                      })}
                                                    </strong>
                                                  </td>
                                                </tr>
                                              )
                                            })}
                                          </tbody>
                                          <tfoot className="table-light">
                                            <tr>
                                              <td colSpan="2" className="fw-bold">Subtotal:</td>
                                              <td colSpan="2" className="text-end fw-bold">
                                                {Number(sale.subtotal || sale.total || 0).toLocaleString(undefined, {
                                                  style: 'currency',
                                                  currency: 'USD'
                                                })}
                                              </td>
                                            </tr>
                                            {sale.discount && sale.discount > 0 && (
                                              <tr>
                                                <td colSpan="2" className="fw-bold">Discount:</td>
                                                <td colSpan="2" className="text-end fw-bold text-danger">
                                                  -{Number(sale.discount).toLocaleString(undefined, {
                                                    style: 'currency',
                                                    currency: 'USD'
                                                  })}
                                                </td>
                                              </tr>
                                            )}
                                            <tr>
                                              <td colSpan="2" className="fw-bold">Total:</td>
                                              <td colSpan="2" className="text-end fw-bold">
                                                {Number(sale.total || 0).toLocaleString(undefined, {
                                                  style: 'currency',
                                                  currency: 'USD'
                                                })}
                                              </td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    ) : (
                                      <p className="text-muted mb-0">No items found</p>
                                    )}
                                    {sale.customer && (
                                      <div className="mt-3">
                                        <small className="text-muted">
                                          <strong>Customer:</strong> {sale.customer.name || 'Walk-in customer'}
                                        </small>
                                      </div>
                                    )}
                                    {sale.cashier_name && (
                                      <div className="mt-1">
                                        <small className="text-muted">
                                          <strong>Cashier:</strong> {sale.cashier_name}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-light">
                          <td colSpan="4" className="fw-bold">Total:</td>
                          <td className="text-end fw-bold">
                            {Number(offlineSales.reduce((sum, sale) => sum + (parseFloat(sale.total) || 0), 0)).toLocaleString(undefined, {
                              style: 'currency',
                              currency: 'USD'
                            })}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Profile

